import type { CameraFeature } from '@tsr/shared-types';
import { isSnapshotCamera } from './greenMotion';

/**
 * Camera clustering core (tracker #195, control-panel spec §1 "Camera clustering
 * + co-located behaviour"). Pure, framework-free so it's unit-testable and reused
 * straight by MapView's camera render path.
 *
 * As the operator adds cameras, plotting every one as its own dot turns dense
 * areas into an unreadable blob. Instead nearby cameras collapse into ONE cluster
 * marker carrying a count; zooming in shrinks the merge radius so clusters break
 * apart. Cameras that sit on a single site (the 3 Parry Sound angles on one dock,
 * the ~7 Canmore townsite cams) are too tight for zoom to ever separate — those
 * are flagged `coLocated` so the COP can drop a selectable list into the rail
 * instead of zooming forever.
 *
 * Clusters are AMBIENT (design-council motion law): the marker never pulses, and
 * its colour is paired with the count label — see MapView's cluster render.
 */

export interface ClusterInput {
  /**
   * Ground radius (m) corresponding to the on-screen cluster pixel radius at the
   * current zoom. Two cameras whose centroid distance is within this merge into
   * one cluster. MapView derives it from the camera eye height + canvas metrics,
   * so it SHRINKS as the operator zooms in (clusters dissolve on the way down).
   */
  clusterRadiusM: number;
  /**
   * Members whose mutual spread (max pairwise distance) is at or below this are
   * treated as CO-LOCATED — physically one site that zoom can't pull apart. A
   * co-located cluster opens the rail pick-list instead of a zoom-in. Fixed
   * ground distance (not zoom-derived) so the "one dock / one yard" classification
   * is stable regardless of altitude.
   */
  coLocatedRadiusM: number;
}

export interface CameraCluster {
  /**
   * Stable id derived from the sorted member ids, so the same grouping yields the
   * same id across re-clusters (lets the renderer reuse / diff entities). A
   * single-member cluster's id is just that camera's id.
   */
  id: string;
  /** Cluster centroid (mean of member coordinates) — where the marker is drawn. */
  lon: number;
  lat: number;
  members: CameraFeature[];
  /**
   * True only for multi-member clusters whose spread ≤ coLocatedRadiusM. Drives
   * the click behaviour: co-located → rail pick-list; otherwise → zoom to expand.
   */
  coLocated: boolean;
}

const EARTH_RADIUS_M = 6_371_000;

/** Great-circle distance between two lon/lat points, in metres. */
export function haversineMeters(
  aLon: number,
  aLat: number,
  bLon: number,
  bLat: number,
): number {
  const toRad = Math.PI / 180;
  const dLat = (bLat - aLat) * toRad;
  const dLon = (bLon - aLon) * toRad;
  const lat1 = aLat * toRad;
  const lat2 = bLat * toRad;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

interface Accumulator {
  members: CameraFeature[];
  sumLon: number;
  sumLat: number;
}

/** Max pairwise distance (m) among a group's members — the group's spread. */
function maxPairwiseMeters(members: CameraFeature[]): number {
  let max = 0;
  for (let i = 0; i < members.length; i += 1) {
    const [iLon, iLat] = members[i]!.geometry.coordinates;
    for (let j = i + 1; j < members.length; j += 1) {
      const [jLon, jLat] = members[j]!.geometry.coordinates;
      const d = haversineMeters(iLon, iLat, jLon, jLat);
      if (d > max) max = d;
    }
  }
  return max;
}

/**
 * Group cameras into clusters by proximity at the current zoom.
 *
 * Greedy leader clustering: cameras are visited in a deterministic order (sorted
 * by id) and each joins the first existing cluster whose running centroid is
 * within `clusterRadiusM`, else it seeds a new cluster. Stable + order-independent
 * of the input array, which keeps the unit tests deterministic and the on-map
 * grouping from flickering between refreshes.
 *
 * A single camera is returned as a one-member cluster (coLocated:false) so the
 * caller has a uniform shape — the renderer treats one-member clusters as a normal
 * camera marker and multi-member clusters as a count marker.
 */
export function clusterCameras(
  cameras: readonly CameraFeature[],
  input: ClusterInput,
): CameraCluster[] {
  const { clusterRadiusM, coLocatedRadiusM } = input;
  const sorted = [...cameras].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const accs: Accumulator[] = [];

  for (const cam of sorted) {
    const [lon, lat] = cam.geometry.coordinates;
    let placed = false;
    for (const acc of accs) {
      const cLon = acc.sumLon / acc.members.length;
      const cLat = acc.sumLat / acc.members.length;
      if (haversineMeters(lon, lat, cLon, cLat) <= clusterRadiusM) {
        acc.members.push(cam);
        acc.sumLon += lon;
        acc.sumLat += lat;
        placed = true;
        break;
      }
    }
    if (!placed) {
      accs.push({ members: [cam], sumLon: lon, sumLat: lat });
    }
  }

  return accs.map((acc) => {
    const memberIds = acc.members.map((m) => m.id).sort();
    const multi = acc.members.length > 1;
    return {
      id: multi ? `cl:${memberIds.join('|')}` : memberIds[0]!,
      lon: acc.sumLon / acc.members.length,
      lat: acc.sumLat / acc.members.length,
      members: acc.members,
      coLocated: multi && maxPairwiseMeters(acc.members) <= coLocatedRadiusM,
    };
  });
}

/**
 * Cluster the live-VIDEO family and the TRAFFIC (snapshot) family SEPARATELY,
 * then concatenate (traffic first) — #385.
 *
 * #368 only made a MIXED cluster RENDER green (precedence recolour); the families
 * could still merge, so a live camera got physically absorbed into a traffic
 * cluster's count (and vice-versa) and was no longer individually selectable.
 * Partitioning by {@link isSnapshotCamera} BEFORE {@link clusterCameras} runs
 * guarantees every resulting cluster is single-family: a live cam never shares a
 * cluster with a snapshot, and a snapshot never hides a live cam. Traffic-first
 * ordering is a courtesy to {@link greenMotion.orderClustersLiveLast} (which the
 * caller still applies), keeping green's last-paint precedence intact.
 */
export function clusterCamerasByFamily(
  cameras: readonly CameraFeature[],
  input: ClusterInput,
): CameraCluster[] {
  const traffic = cameras.filter(isSnapshotCamera);
  const live = cameras.filter((c) => !isSnapshotCamera(c));
  return [...clusterCameras(traffic, input), ...clusterCameras(live, input)];
}

/**
 * Ground metres under one screen pixel for a top-down view at `eyeHeightM`, given
 * the vertical canvas extent + vertical field-of-view. MapView feeds this the live
 * Cesium frustum so the cluster merge radius tracks the actual zoom. Defensive
 * against a zero-height canvas (headless / pre-layout) → returns 0.
 */
export function metersPerPixel(
  eyeHeightM: number,
  canvasHeightPx: number,
  fovyRad: number,
): number {
  if (canvasHeightPx <= 0) return 0;
  const groundExtentM = 2 * eyeHeightM * Math.tan(fovyRad / 2);
  return groundExtentM / canvasHeightPx;
}
